<template>
  <div class="popup-upload-container">
    <!-- Box Mode (for Popup Image) -->
    <div v-if="mode === 'box'" class="upload-box" :style="boxStyle">
      <template v-if="!value">
        <Upload
          ref="upload"
          action="/"
          :before-upload="handleBeforeUpload"
          :show-upload-list="false"
          accept=".jpg,.jpeg,.png"
          :max-size="maxSize"
        >
          <div class="upload-trigger">
            <Button type="primary">{{ title || '点击上传图片' }}</Button>
            <div v-if="displayHint" class="upload-hint">{{ displayHint }}</div>
          </div>
        </Upload>
      </template>
      <template v-else>
        <div class="preview-wrapper">
          <img :src="value" class="preview-img" />
          <div class="preview-cover">
            <Icon type="ios-eye-outline" size="24" @click="handlePreview" />
            <Icon type="ios-trash-outline" size="24" @click="handleRemove" />
          </div>
        </div>
      </template>
      <div v-if="uploadLoading" class="upload-loading-mask">
        <Spin fix>
          <Icon type="ios-loading" size="24" class="spin-icon-load"></Icon>
          <div>上传中 {{ percent }}%</div>
        </Spin>
      </div>
    </div>

    <!-- Button Mode (for Button Image or simple upload) -->
    <div v-else-if="mode === 'button'" class="upload-button-mode">
      <Upload
        ref="upload"
        action="/"
        :before-upload="handleBeforeUpload"
        :show-upload-list="false"
        accept=".jpg,.jpeg,.png"
        :max-size="maxSize"
      >
        <Button 
          type="primary" 
          :loading="uploadLoading" 
          :long="long" 
          icon="ios-cloud-upload-outline"
        >
          {{ value ? '重新上传' : (title || '点击上传') }}
        </Button>
      </Upload>
      <div v-if="value && !hideButtonPreview" class="button-preview-mini">
        <img :src="value" @click="handlePreview" />
        <Icon type="ios-close-circle" size="16" class="btn-del" @click="handleRemove" />
      </div>
    </div>

    <!-- Custom Mode (Slot for trigger) -->
    <div v-else class="upload-custom-mode">
       <Upload
        ref="upload"
        action="/"
        :before-upload="handleBeforeUpload"
        :show-upload-list="false"
        accept=".jpg,.jpeg,.png"
        :max-size="maxSize"
      >
        <slot></slot>
      </Upload>
    </div>
  </div>
</template>

<script>
import { upload } from '@/api/common'

export default {
  name: 'PopupUpload',
  props: {
    value: {
      type: String,
      default: ''
    },
    mode: {
      type: String,
      default: 'box' // 'box' or 'button'
    },
    title: String,
    hint: String,
    width: {
      type: [String, Number],
      default: '240px'
    },
    height: {
      type: [String, Number],
      default: '320px'
    },
    long: {
      type: Boolean,
      default: false
    },
    hideButtonPreview: {
      type: Boolean,
      default: false
    },
    maxSize: {
      type: Number,
      default: 2048
    },
    exactWidth: Number,
    exactHeight: Number
  },
  data() {
    return {
      uploadLoading: false,
      percent: 0
    }
  },
  computed: {
    boxStyle() {
      if (this.mode !== 'box') return {}
      return {
        width: typeof this.width === 'number' ? this.width + 'px' : this.width,
        height: typeof this.height === 'number' ? this.height + 'px' : this.height
      }
    },
    maxSizeText() {
      if (!this.maxSize) return ''
      if (this.maxSize < 1024) return `${this.maxSize}KB`
      const mb = this.maxSize / 1024
      return Number.isInteger(mb) ? `${mb}MB` : `${mb.toFixed(1)}MB`
    },
    displayHint() {
      if (!this.hint) return ''
      if (!this.maxSize || this.maxSize === 2048) return this.hint
      return this.hint.replace(/(\d+(\.\d+)?)\s*(kb|mb)/gi, this.maxSizeText)
    }
  },
  methods: {
    handleBeforeUpload(file) {
      if (this.maxSize && file.size / 1024 > this.maxSize) {
        this.$Message.error(`图片大小不能超过${this.maxSizeText}`)
        return false
      }

      const checkSize = (file) => {
        return new Promise((resolve, reject) => {
          if (!this.exactWidth && !this.exactHeight) {
            resolve()
            return
          }
          const reader = new FileReader()
          reader.readAsDataURL(file)
          reader.onload = (e) => {
             const img = new Image()
             img.src = e.target.result
             img.onload = () => {
               // Check Ratio if both dimensions are provided
               if (this.exactWidth && this.exactHeight) {
                  if (img.width !== this.exactWidth || img.height !== this.exactHeight) {
                    this.$Message.error(`图片尺寸不正确 (需 ${this.exactWidth}x${this.exactHeight})`)
                    reject()
                  } else {
                    resolve()
                  }
               }
               // Check strict Width if only width provided
               else if (this.exactWidth && img.width !== this.exactWidth) {
                 this.$Message.error(`图片宽度必须为 ${this.exactWidth}px`)
                 reject()
               } 
               // Check strict Height if only height provided
               else if (this.exactHeight && img.height !== this.exactHeight) {
                 this.$Message.error(`图片高度必须为 ${this.exactHeight}px`)
                 reject()
               } else {
                 resolve()
               }
             }
          }
        })
      }

      checkSize(file).then(() => {
        const formData = new FormData()
        formData.append('file', file)
        this.uploadLoading = true
        this.percent = 0
        
        const config = {
          onUploadProgress: progressEvent => {
            this.percent = Math.round((progressEvent.loaded * 100) / progressEvent.total)
          }
        }

        upload(formData, config).then(({ data }) => {
          this.uploadLoading = false
          // Fix: emit the URL string for v-model, passing full object to on-success
          // data structure usually: { id: ..., fileUrl: ... }
          this.$emit('input', data.fileUrl) 
          this.$emit('on-success', data)
        }).catch(() => {
          this.uploadLoading = false
          this.$Message.error('上传失败')
        })
      }).catch(() => {
        // Validation failed, do nothing
      })

      return false // Stop default auto-upload
    },
    handleRemove() {
      this.$emit('input', '')
      this.$emit('on-remove')
    },
    handlePreview() {
      if (this.value) {
        // Use iview viewer or global viewer if available
        if (this.$viewerApi) {
          this.$viewerApi({ images: [this.value] })
        } else {
          window.open(this.value, '_blank')
        }
      }
    }
  }
}
</script>

<style scoped lang="less">
.popup-upload-container {
  display: inline-block;
  vertical-align: top;
}

.upload-box {
  background: #f8f8f9;
  border: 1px dashed #dcdee2;
  border-radius: 4px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  transition: border-color 0.2s;

  &:hover {
    border-color: #2d8cf0;
  }

  .upload-trigger {
    text-align: center;
    padding: 20px;
    
    .upload-hint {
      margin-top: 10px;
      color: #999;
      font-size: 12px;
      line-height: 1.4;
    }
  }

  .preview-wrapper {
    width: 100%;
    height: 100%;
    position: relative;

    .preview-img {
      width: 100%;
      height: 100%;
      object-fit: contain;
      display: block;
    }

    .preview-cover {
      display: none;
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;
      background: rgba(0, 0, 0, 0.6);
      align-items: center;
      justify-content: center;
      gap: 20px;
      i {
        color: #fff;
        cursor: pointer;
        &:hover { opacity: 0.8; }
      }
    }

    &:hover .preview-cover {
      display: flex;
    }
  }
}

.upload-button-mode {
  display: flex;
  flex-direction: column; /* Change to column to put preview below */
  align-items: flex-start;
  gap: 10px;
  
  .button-preview-mini {
    position: relative;
    width: auto; /* Fit content */
    display: inline-block; /* Wrap around image */
    height: auto;
    border: 1px solid #dcdee2;
    border-radius: 2px;
    margin-top: 10px;
    
    img {
      max-width: 150px; /* Allow slightly wider preview for buttons */
      height: auto;
      display: block;
      cursor: pointer;
    }
    .btn-del {
      position: absolute;
      top: -8px;
      right: -8px;
      background: #fff;
      color: #ed4014;
      cursor: pointer;
      border-radius: 50%;
      border: 1px solid #dcdee2;
      width: 18px; /* Fixed size for delete buttom */
      height: 18px;
      line-height: 16px; /* Center icon */
      text-align: center;
      font-size: 14px;
      z-index: 10;
    }
  }
}

.upload-loading-mask {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.8);
  z-index: 10;
}

.spin-icon-load {
  animation: ani-spin 1s linear infinite;
}
@keyframes ani-spin {
  from { transform: rotate(0deg); }
  50% { transform: rotate(180deg); }
  to { transform: rotate(360deg); }
}
</style>
